import React, {useEffect, useState} from 'react'
import {toast} from 'react-toastify'
import {toastDefaultConfig} from '../../../shared/config/toastConfig'
import {getLogger} from '../../../shared/utils/logUtils'
import {
  getSessionExpirationHours,
  isSessionExpirationCognitoWriteForbidden,
  normalizeSessionExpirationHours,
  setSessionExpirationHours,
} from '../../../shared/utils/sessionExpirationPreference'
import * as authHelper from '../auth/core/AuthHelpers'
import {useCognitoAuth} from '../auth/core/CognitoAuth'

const PRESETS: {hours: number; label: string; hint: string}[] = [
  {
    hours: 0,
    label: 'Stay signed in',
    hint: 'Remain logged in until you sign out manually.',
  },
  {
    hours: 24,
    label: '24 hours',
    hint: 'Automatically sign out after 24 hours. Recommended for shared devices.',
  },
  {
    hours: 30 * 24,
    label: '30 days',
    hint: 'Automatically sign out after 30 days.',
  },
]

export const SessionPreferences: React.FC = () => {
  const {currentUser, setCurrentUser, cognitoAuth} = useCognitoAuth()
  const [hours, setHours] = useState<number>(() =>
    normalizeSessionExpirationHours(
      currentUser?.sessionExpirationHours ?? getSessionExpirationHours()
    )
  )
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const v = currentUser?.sessionExpirationHours
    if (v !== undefined) {
      const n = normalizeSessionExpirationHours(v)
      setHours(n)
      setSessionExpirationHours(n)
    }
  }, [currentUser?.sessionExpirationHours])

  const onChange = async (nextHours: number) => {
    const normalized = normalizeSessionExpirationHours(nextHours)
    if (normalized === hours) {
      return
    }
    if (!cognitoAuth) {
      setSessionExpirationHours(normalized)
      setHours(normalized)
      setCurrentUser((prev) => (prev ? {...prev, sessionExpirationHours: normalized} : prev))
      return
    }
    const previous = hours
    setHours(normalized)
    setSaving(true)
    try {
      await authHelper.updateUserAttributes({sessionExpirationHours: normalized})
      setSessionExpirationHours(normalized)
      setCurrentUser((prev) => (prev ? {...prev, sessionExpirationHours: normalized} : prev))
    } catch (e: unknown) {
      if (isSessionExpirationCognitoWriteForbidden(e)) {
        setSessionExpirationHours(normalized)
        setCurrentUser((prev) => (prev ? {...prev, sessionExpirationHours: normalized} : prev))
        toast(
          'Saved on this device only. Your Cognito app client must be allowed to write ' +
            'custom:session_expiration (and the attribute must exist on the user pool) to sync ' +
            'across devices. Defaults stay in effect until then.',
          {...toastDefaultConfig, type: 'warning'}
        )
        getLogger().warn('Session duration not writable on Cognito; using local preference only', e)
      } else {
        setHours(previous)
        toast(
          e instanceof Error
            ? e.message
            : 'Could not save session preference. If this persists, contact support.',
          {...toastDefaultConfig, type: 'error'}
        )
        getLogger().error(e)
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className='card mb-5 mb-xl-10'>
      <div
        className='card-header border-0 cursor-pointer'
        role='button'
        data-bs-toggle='collapse'
        data-bs-target='#kt_account_session_preferences'
      >
        <div className='card-title m-0'>
          <h3 className='fw-bolder m-0'>
            <i className='fas fa-clock me-2' />
            Session duration
          </h3>
        </div>
      </div>

      <div id='kt_account_session_preferences' className='collapse show'>
        <div className='card-body border-top p-9'>
          <div className='fv-row'>
            {PRESETS.map((opt) => (
              <label
                key={opt.hours}
                className='form-check form-check-custom form-check-solid mb-6 d-flex align-items-start'
              >
                <input
                  className='form-check-input mt-1'
                  type='radio'
                  name='session_expiration_hours'
                  value={opt.hours}
                  checked={hours === opt.hours}
                  disabled={saving}
                  onChange={() => void onChange(opt.hours)}
                />
                <span className='form-check-label fw-bold text-gray-800 ms-3'>
                  {opt.label}
                  <span className='fw-normal text-gray-600 d-block fs-7 mt-1'>{opt.hint}</span>
                </span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
